import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  PageHeader, StatTile, Panel, Banner, Button, Field, FormRow, Textarea, DefinitionList, DataTable,
  AreaChart, BarChart, Sparkline, SegmentedControl, useToast, type Column,
} from '../ds/index'
import { useApi } from '../lib/useApi'
import { api } from '../lib/api'
import { money, num } from '../lib/format'
import { SplitBar, HBars } from '../components/Viz'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

export type Economics = {
  org: { slug: string; name: string; timezone: string }
  month: string; as_of: string; demo_data: boolean; terms_set: boolean
  subscribers: { active: number; without_price: number }
  leads_without_price: number
  ai_calls_this_month: number
  revenue: { org_lead_revenue_cents: number; leads_delivered: number; revenue_per_lead_cents: number | null; rev_share_bps: number; share_cents: number; monthly_minimum_cents: number; minimum_applies: boolean; spec_monthly_cents: number; spec_per_lead_cents: number | null }
  partner: { name: string | null; share_bps: number; monthly_cents: number }
  costs: { ai_month_to_date_cents: number; ai_projected_month_cents: number | null; hosting_monthly_cents: number; other_monthly_cents: number; other_note: string | null; monthly_cents: number }
  net: { monthly_cents: number; margin_bps: number | null }
  stripe_fee: {
    on: boolean; bps: number; agreed_bps: number; switched_at: string | null; connected: boolean; all_time_fees_cents: number
    month: { payments: number; gross_cents: number; fees_cents: number; card_deposits_cents: number; manual_deposits_cents: number; manual_share_cents: number }
    partner: {
      name: string | null; share_bps: number; of_payment_bps: number; account_id: string | null; label: string | null
      connected_at: string | null; confirmed_at: string | null
      sent_month_cents: number; sent_all_cents: number; waiting_cents: number; failed: number; last_error: string | null
    }
  }
  terms: null | { billing_starts_on: string | null; rev_share_bps: number; monthly_minimum_cents: number; partner_name: string | null; partner_share_bps: number; hosting_monthly_cents: number; other_monthly_cents: number; other_note: string | null; notes: string | null; updated_at: string }
}

export type MonthRow = {
  month: string; is_current: boolean; billing: boolean
  lead_revenue_cents: number; leads_delivered: number; revenue_growth_bps: number | null
  subscribers_active: number; subscribers_started: number; subscribers_ended: number
  spec_cents: number; partner_cents: number; ai_cents: number; running_cents: number; costs_cents: number; net_cents: number
}
type History = { months: MonthRow[]; revenue_by_category: { category: string; revenue_cents: number; leads: number; subscribers: number }[] }

/** Axis ticks stay short; tooltips and tables get the exact figure. */
const axisMoney = (v: number | string) => {
  const d = Number(v) / 100
  if (Math.abs(d) >= 1000) return `$${(d / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k`
  return `$${d.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}
const shortMonth = (m: string) => new Date(`${m}-15T12:00:00`).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
const growth = (bps: number | null) => bps == null ? '' : `${bps > 0 ? '+' : ''}${(bps / 100).toFixed(1)}%`

export const bpsPct = (bps: number | null | undefined) => bps == null ? 'n/a' : `${(bps / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`
const monthName = (m: string) => new Date(`${m}-15T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

/** The five numbers, used on this page and on the SPEC view of the dashboard. */
export function EconomicsTiles({ e, compact = false }: { e: Economics; compact?: boolean }) {
  const partner = e.partner.name || 'Partner'
  return (
    <div className="grid-tiles five" style={{ marginBottom: compact ? 0 : 'var(--s-7)' }}>
      <StatTile label={`${e.org.name} lead revenue`} value={money(e.revenue.org_lead_revenue_cents)}
                meta={`${num(e.revenue.leads_delivered)} leads delivered this month, ${num(e.subscribers.active)} active buyers`} />
      <StatTile focal label="SPEC is paid" value={money(e.revenue.spec_monthly_cents)}
                meta={e.revenue.minimum_applies ? `${money(e.revenue.monthly_minimum_cents)} minimum applies` : `${bpsPct(e.revenue.rev_share_bps)} of lead revenue${e.revenue.spec_per_lead_cents ? `, ${money(e.revenue.spec_per_lead_cents)} a lead` : ''}`} />
      <StatTile label={`${partner}'s share`} value={money(e.partner.monthly_cents)} meta={`${bpsPct(e.partner.share_bps)} of what SPEC is paid`} />
      <StatTile label="Costs, this month" value={money(e.costs.monthly_cents)}
                meta={e.costs.ai_projected_month_cents == null ? `AI ${money(e.costs.ai_month_to_date_cents)} so far` : `AI ${money(e.costs.ai_projected_month_cents)} projected`} />
      <StatTile label="SPEC keeps" value={money(e.net.monthly_cents)} meta={e.net.margin_bps == null ? 'no revenue yet' : `${bpsPct(e.net.margin_bps)} of what SPEC is paid`} />
    </div>
  )
}

export default function EconomicsPage() {
  const { data, error, reload } = useApi<Economics>('/api/spec/economics')
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const e = data
  const partner = e.partner.name || 'Partner'

  return (
    <>
      <PageHeader title="Economics" subtitle={`SPEC only. What ${e.org.name} pays SPEC, what goes to ${partner}, and what running it costs. ${monthName(e.month)}, in ${e.org.timezone.replace('_', ' ')}.`} />
      {e.demo_data && (
        <div style={{ marginBottom: 'var(--s-6)' }}>
          <Banner tone="warn" title="Revenue here is from invented demo subscribers">
            The subscriber prices on this install are made up for the demo, so the revenue, {partner}'s share and what SPEC keeps are not real yet. The terms and the AI cost are real.
          </Banner>
        </div>
      )}
      {!e.terms_set && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="warn" title="No terms set">Nothing is earned until the terms below are saved.</Banner></div>}
      {e.subscribers.without_price > 0 && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="warn" title={`${e.subscribers.without_price} active buyer${e.subscribers.without_price === 1 ? ' has' : 's have'} no price per lead`}>Leads delivered to them earn nothing until a price is set on the Buyers screen. {e.leads_without_price > 0 ? `${e.leads_without_price} lead${e.leads_without_price === 1 ? '' : 's'} delivered this month went out unpriced.` : ''}</Banner></div>}
      <EconomicsTiles e={e} />
      <StripeFeePanel e={e} onSaved={reload} />
      <GrowthSection orgName={e.org.name} partner={partner} />
      <div className="two-col">
        <Panel>
          <div className="panel-header"><h2 className="panel-title">This month</h2><span className="muted">as of {new Date(e.as_of).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span></div>
          <DefinitionList items={[
            { label: `${e.org.name} lead revenue`, value: money(e.revenue.org_lead_revenue_cents), numeric: true },
            { label: `${bpsPct(e.revenue.rev_share_bps)} share`, value: money(e.revenue.share_cents), numeric: true },
            { label: 'Monthly minimum', value: `${money(e.revenue.monthly_minimum_cents)}${e.revenue.minimum_applies ? ' (applies)' : ''}`, numeric: true },
            { label: 'SPEC is paid', value: money(e.revenue.spec_monthly_cents), numeric: true },
            { label: `${partner}, ${bpsPct(e.partner.share_bps)}`, value: `minus ${money(e.partner.monthly_cents)}`, numeric: true },
            { label: `AI, ${num(e.ai_calls_this_month)} calls so far`, value: `${money(e.costs.ai_month_to_date_cents)} so far${e.costs.ai_projected_month_cents == null ? '' : `, ${money(e.costs.ai_projected_month_cents)} projected`}`, numeric: true },
            { label: 'Hosting', value: e.costs.hosting_monthly_cents ? `minus ${money(e.costs.hosting_monthly_cents)}` : 'not entered', numeric: true },
            { label: e.costs.other_note || 'Other running costs', value: e.costs.other_monthly_cents ? `minus ${money(e.costs.other_monthly_cents)}` : 'not entered', numeric: true },
            { label: 'SPEC keeps', value: money(e.net.monthly_cents), numeric: true },
          ]} />
          <p className="muted" style={{ fontSize: 'var(--t-ui)', marginTop: 'var(--s-5)' }}>
            Lead revenue is what the buyers were billed for the leads delivered this month, each at the price stamped on it when the rep replied. AI cost is every real model call, priced from its own token counts; the same calls show in Anthropic's console under the HandPicked workspace. <Link to="/costs">AI cost detail</Link>
          </p>
        </Panel>
        <TermsForm e={e} onSaved={reload} />
      </div>
    </>
  )
}

function TermsForm({ e, onSaved }: { e: Economics; onSaved: () => void }) {
  const toast = useToast()
  const t = e.terms
  const toForm = () => ({
    starts: t?.billing_starts_on || '',
    share: t ? String(t.rev_share_bps / 100) : '', minimum: t ? String(t.monthly_minimum_cents / 100) : '',
    partner_name: t?.partner_name || '', partner_share: t ? String(t.partner_share_bps / 100) : '',
    hosting: t ? String(t.hosting_monthly_cents / 100) : '', other: t ? String(t.other_monthly_cents / 100) : '',
    other_note: t?.other_note || '', notes: t?.notes || '',
  })
  const [f, setF] = useState(toForm)
  const [busy, setBusy] = useState(false)
  useEffect(() => { setF(toForm()) }, [t?.updated_at])
  const set = (k: keyof ReturnType<typeof toForm>, v: string) => setF((s) => ({ ...s, [k]: v }))
  const bps = (v: string) => Math.round(Number(v || 0) * 100)
  const cents = (v: string) => Math.round(Number(v || 0) * 100)

  const save = async () => {
    setBusy(true)
    try {
      await api('/api/spec/deal-terms', { method: 'PUT', body: JSON.stringify({
        billing_starts_on: f.starts || null,
        rev_share_bps: bps(f.share), monthly_minimum_cents: cents(f.minimum), partner_name: f.partner_name,
        partner_share_bps: bps(f.partner_share), hosting_monthly_cents: cents(f.hosting), other_monthly_cents: cents(f.other),
        other_note: f.other_note, notes: f.notes,
      }) })
      toast('Terms saved', 'pos')
      onSaved()
    } catch (err) { toast((err as Error).message, 'neg') } finally { setBusy(false) }
  }

  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Terms</h2>{t && <span className="muted">updated {new Date(t.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}</div>
      <div className="stack-tight">
        <div className="two-col" style={{ marginBottom: 0 }}>
          <Field label="SPEC's pay starts" type="date" value={f.starts} onChange={(ev) => set('starts', ev.target.value)} />
          <span />
          <Field label="SPEC share of lead revenue, %" type="number" step="0.01" min={0} max={100} value={f.share} onChange={(ev) => set('share', ev.target.value)} />
          <Field label="Monthly minimum, $" type="number" step="1" min={0} value={f.minimum} onChange={(ev) => set('minimum', ev.target.value)} />
          <Field label="Partner" value={f.partner_name} onChange={(ev) => set('partner_name', ev.target.value)} />
          <Field label="Partner share of what SPEC is paid, %" type="number" step="0.01" min={0} max={100} value={f.partner_share} onChange={(ev) => set('partner_share', ev.target.value)} />
          <Field label="Hosting, $ a month" type="number" step="0.01" min={0} value={f.hosting} onChange={(ev) => set('hosting', ev.target.value)} />
          <Field label="Other costs, $ a month" type="number" step="0.01" min={0} value={f.other} onChange={(ev) => set('other', ev.target.value)} />
        </div>
        <Field label="What the other costs are" value={f.other_note} onChange={(ev) => set('other_note', ev.target.value)} placeholder="Google Workspace inboxes" />
        <FormRow label="Notes"><Textarea rows={2} value={f.notes} onChange={(ev) => set('notes', ev.target.value)} /></FormRow>
        <div><Button onClick={save} disabled={busy}>{busy ? 'Saving' : 'Save terms'}</Button></div>
      </div>
    </Panel>
  )
}

function GrowthSection({ orgName, partner }: { orgName: string; partner: string }) {
  const [range, setRange] = useState('12')
  const { data, error } = useApi<History>(`/api/spec/economics/history?months=${range}`)
  if (error) return <div style={{ marginBottom: 'var(--s-7)' }}><ErrorBlock error={error} /></div>
  if (!data) return <div style={{ marginBottom: 'var(--s-7)' }}><LoadingBlock rows={4} /></div>

  const months = data.months
  // Axis labels stay short so they never collide; the subtitle says the last month is partial.
  const label = (m: MonthRow) => shortMonth(m.month)
  const revenueData = months.map((m) => ({ month: label(m), revenue: m.lead_revenue_cents }))
  const billed = months.filter((m) => m.billing)
  const payData = billed.map((m) => ({
    month: label(m),
    keeps: Math.max(0, m.net_cents),
    partner: m.partner_cents,
    costs: m.costs_cents,
  }))
  const subsData = months.map((m) => ({ month: label(m), started: m.subscribers_started, ended: m.subscribers_ended }))
  const cur = months[months.length - 1]
  const prev = months.length > 1 ? months[months.length - 2] : null
  const firstBilled = billed[0]
  const specTotal = billed.reduce((a, m) => a + m.spec_cents, 0)
  const partnerTotal = billed.reduce((a, m) => a + m.partner_cents, 0)
  const netTotal = billed.reduce((a, m) => a + m.net_cents, 0)

  const cols: Column<MonthRow>[] = [
    { key: 'month', header: 'Month', cell: (m) => <span className="nowrap"><strong>{shortMonth(m.month)}</strong>{m.is_current && <span className="muted"> so far</span>}</span>, sortBy: (m) => m.month },
    { key: 'revenue', header: `${orgName} lead revenue`, numeric: true, cell: (m) => money(m.lead_revenue_cents), sortBy: (m) => m.lead_revenue_cents },
    { key: 'leads', header: 'Leads', numeric: true, cell: (m) => num(m.leads_delivered), sortBy: (m) => m.leads_delivered },
    { key: 'growth', header: 'Growth', numeric: true, cell: (m) => m.revenue_growth_bps == null ? <span className="muted">n/a</span> : growth(m.revenue_growth_bps), sortBy: (m) => m.revenue_growth_bps ?? 0 },
    { key: 'active', header: 'Active', numeric: true, cell: (m) => num(m.subscribers_active) },
    { key: 'moves', header: 'Signed / cancelled', numeric: true, cell: (m) => `${m.subscribers_started} / ${m.subscribers_ended}` },
    { key: 'spec', header: 'SPEC paid', numeric: true, cell: (m) => m.billing ? money(m.spec_cents) : <span className="muted">not started</span>, sortBy: (m) => m.spec_cents },
    { key: 'partner', header: partner, numeric: true, cell: (m) => m.billing ? money(m.partner_cents) : '' },
    { key: 'costs', header: 'Costs', numeric: true, cell: (m) => money(m.costs_cents) },
    { key: 'net', header: 'SPEC keeps', numeric: true, cell: (m) => m.billing ? <strong>{money(m.net_cents)}</strong> : '', sortBy: (m) => m.net_cents },
  ]

  return (
    <div style={{ marginBottom: 'var(--s-7)' }}>
      <div className="spread" style={{ margin: 'var(--s-7) 0 var(--s-5)' }}>
        <h2 className="panel-title" style={{ margin: 0 }}>By month</h2>
        <SegmentedControl value={range} onChange={setRange} segments={[{ value: '6', label: '6 months' }, { value: '12', label: '12 months' }, { value: '24', label: '24 months' }]} />
      </div>
      <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
        <StatTile label="Revenue growth this month" value={cur?.revenue_growth_bps == null ? 'n/a' : growth(cur.revenue_growth_bps)}
                  meta={<><div>{prev ? `from ${money(prev.lead_revenue_cents)} in ${shortMonth(prev.month)}` : 'first month on record'}</div><Sparkline data={revenueData} valueKey="revenue" /></>} />
        <StatTile label="Subscribers" value={num(cur?.subscribers_active ?? 0)}
                  meta={`${months.reduce((a, m) => a + m.subscribers_started, 0)} signed, ${months.reduce((a, m) => a + m.subscribers_ended, 0)} cancelled in this window`} />
        <StatTile label={firstBilled ? `SPEC paid since ${shortMonth(firstBilled.month)}` : 'SPEC paid'} value={money(specTotal)} meta={`${partner} ${money(partnerTotal)}`} />
        <StatTile label="SPEC kept" value={money(netTotal)} meta={billed.length ? `${billed.length} month${billed.length === 1 ? '' : 's'} billed` : 'billing has not started'} />
      </div>
      <div className="two-col" style={{ marginBottom: 'var(--s-6)' }}>
        <Panel>
          <AreaChart title={`${orgName} lead revenue`} subtitle="What buyers were billed for delivered leads, month by month. This month is so far." data={revenueData} xKey="month"
                     series={[{ key: 'revenue', label: 'Lead revenue' }]} format={axisMoney} height={240} showTable />
        </Panel>
        <Panel>
          {payData.length >= 2 ? (
            <BarChart title="Where SPEC's pay goes" subtitle={`Each bar is what SPEC is paid that month: what it keeps, ${partner}'s share, and running costs`}
                      data={payData} xKey="month" stacked format={axisMoney} height={240} showTable
                      series={[{ key: 'keeps', label: 'SPEC keeps' }, { key: 'partner', label: partner }, { key: 'costs', label: 'Costs' }]} />
          ) : payData.length === 1 ? (
            // One billed month is a part-to-whole, not a chart of one bar. Monthly bars take over from the second month.
            <SplitBar title="Where SPEC's pay goes" subtitle={`${shortMonth(billed[0].month)} so far, the first month billed. Monthly bars appear from the second month.`}
                      headline={<><span className="s-num">{money(billed[0].spec_cents)}</span> <span className="muted">SPEC is paid</span></>}
                      parts={[
                        { label: 'SPEC keeps', value: payData[0].keeps, slot: 1 },
                        { label: partner, value: payData[0].partner, slot: 2 },
                        { label: 'Costs', value: payData[0].costs, slot: 3 },
                      ]} />
          ) : <Banner tone="neutral" title="Billing has not started">Set when SPEC's pay starts in the terms below.</Banner>}
        </Panel>
      </div>
      <div className="two-col" style={{ marginBottom: 'var(--s-6)' }}>
        <Panel>
          <BarChart title="Subscribers signed and cancelled" subtitle="By month, from start and end dates. This month is so far." data={subsData} xKey="month" height={220} showTable
                    series={[{ key: 'started', label: 'Signed' }, { key: 'ended', label: 'Cancelled' }]} />
        </Panel>
        <Panel>
          <HBars title="Lead revenue by category" subtitle="This month, delivered leads" empty="No leads delivered yet this month."
                 rows={data.revenue_by_category.map((c) => ({ label: c.category, value: c.revenue_cents, note: `${c.leads} lead${c.leads === 1 ? '' : 's'}, ${c.subscribers} buyer${c.subscribers === 1 ? '' : 's'}` }))} />
        </Panel>
      </div>
      <Panel className="panel-flush">
        <div className="panel-header" style={{ padding: 'var(--s-5) var(--s-6) 0' }}>
          <h2 className="panel-title">Month by month</h2>
          <span className="muted">past months use each subscriber's current price</span>
        </div>
        <DataTable columns={cols} rows={[...months].reverse()} rowKey={(m) => m.month} />
      </Panel>
    </div>
  )
}

/**
 * SPEC's share taken automatically by Stripe on every card payment: the switch,
 * what Stripe has collected, and what is left to invoice for money that came in
 * another way. SPEC only.
 */
function StripeFeePanel({ e, onSaved }: { e: Economics; onSaved: () => void }) {
  const toast = useToast()
  const f = e.stripe_fee
  const [confirming, setConfirming] = useState(false)
  const [signed, setSigned] = useState(false)
  const [busy, setBusy] = useState(false)
  const agreed = bpsPct(f.agreed_bps)
  const set = async (on: boolean) => {
    setBusy(true)
    try {
      await api('/api/spec/stripe-fee', { method: 'PUT', body: JSON.stringify({ on, agreement_signed: on ? signed : undefined }) })
      toast(on ? `SPEC's ${agreed} is now taken on every card payment` : 'Automatic collection is off', 'pos')
      setConfirming(false); setSigned(false); onSaved()
    } catch (err) { toast((err as Error).message, 'neg') } finally { setBusy(false) }
  }
  const m = f.month
  return (
    <div style={{ marginBottom: 'var(--s-7)' }}>
      <Panel>
        <div className="panel-header">
          <h2 className="panel-title">SPEC's share through Stripe</h2>
          <span className={`pill ${f.on ? 'is-good' : ''}`.trim()}>{f.on ? `On, ${bpsPct(f.bps)}` : 'Off'}</span>
        </div>
        <p style={{ marginTop: 0 }}>
          {f.on
            ? <>Every card payment a client makes to {e.org.name} carries an application fee of {bpsPct(f.bps)}, which Stripe sends to SPEC's own Stripe account. {e.org.name} sees it on each payment in their Stripe dashboard.</>
            : <>When on, Stripe takes SPEC's agreed share ({agreed}, from the terms) out of every card payment a client makes and sends it to SPEC's own Stripe account. Nothing is taken while it is off.</>}
        </p>
        {!f.connected && <p className="muted" style={{ marginTop: 0 }}>{e.org.name} has not connected Stripe yet, so there are no card payments to take it from.</p>}

        <div className="fee-grid">
          <div><span className="hero-kicker">This month, through Stripe</span><strong>{money(m.fees_cents)}</strong><span className="muted">{m.payments} payment{m.payments === 1 ? '' : 's'}, {money(m.gross_cents)} paid by clients</span></div>
          <div><span className="hero-kicker">Paid another way this month</span><strong>{money(m.manual_deposits_cents)}</strong><span className="muted">{m.manual_deposits_cents ? `${agreed} of it is ${money(m.manual_share_cents)}, to invoice` : 'nothing to invoice'}</span></div>
          <div><span className="hero-kicker">Collected through Stripe, all time</span><strong>{money(f.all_time_fees_cents)}</strong><span className="muted">{f.switched_at ? `${f.on ? 'on' : 'off'} since ${new Date(f.switched_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'never switched on'}</span></div>
        </div>

        <SplitLine orgName={e.org.name} agreedBps={f.agreed_bps} partner={f.partner} />
        <PartnerPayouts e={e} onSaved={onSaved} />

        {f.on ? (
          <div className="row-tight" style={{ marginTop: 'var(--s-5)' }}><Button variant="secondary" onClick={() => set(false)} disabled={busy}>Turn off</Button></div>
        ) : !confirming ? (
          <div className="row-tight" style={{ marginTop: 'var(--s-5)' }}><Button onClick={() => setConfirming(true)} disabled={!e.terms_set || f.agreed_bps <= 0}>Take {agreed} automatically</Button>
            {(!e.terms_set || f.agreed_bps <= 0) && <span className="muted">Save SPEC's share in the terms first.</span>}</div>
        ) : (
          <div className="fee-confirm">
            <label className="row-tight" style={{ alignItems: 'flex-start' }}>
              <input type="checkbox" checked={signed} onChange={(ev) => setSigned(ev.target.checked)} style={{ marginTop: 4 }} />
              <span>The agreement with {e.org.name} is signed and says SPEC takes {agreed} of card payments through Stripe.</span>
            </label>
            <p className="muted" style={{ margin: 'var(--s-3) 0 0', fontSize: 13 }}>From the next payment on, {agreed} of every card payment goes to SPEC. Payments already made are not touched.</p>
            <div className="row-tight" style={{ marginTop: 'var(--s-4)' }}>
              <Button onClick={() => set(true)} disabled={busy || !signed}>{busy ? 'Turning on...' : `Turn on ${agreed}`}</Button>
              <Button variant="ghost" onClick={() => { setConfirming(false); setSigned(false) }}>Cancel</Button>
            </div>
          </div>
        )}
      </Panel>
    </div>
  )
}

/** Where each card payment goes, in one line: the company, SPEC, the partner. */
function SplitLine({ orgName, agreedBps, partner }: { orgName: string; agreedBps: number; partner: Economics['stripe_fee']['partner'] }) {
  const p = partner.of_payment_bps
  const parts = [
    { label: orgName, bps: 10000 - agreedBps, cls: 'is-org' },
    { label: 'SPEC', bps: agreedBps - p, cls: 'is-spec' },
    ...(p > 0 ? [{ label: partner.name || 'Partner', bps: p, cls: 'is-partner' }] : []),
  ]
  return (
    <div className="split-line" aria-label="Where each card payment goes">
      <div className="split-bar">{parts.map((x) => <span key={x.label} className={x.cls} style={{ width: `${x.bps / 100}%` }} />)}</div>
      <div className="split-key">{parts.map((x) => <span key={x.label}><i className={x.cls} />{x.label} <strong>{bpsPct(x.bps)}</strong></span>)}</div>
    </div>
  )
}

/** The partner's share sent on to their own Stripe: connect by link, SPEC confirms, then it is automatic. */
function PartnerPayouts({ e, onSaved }: { e: Economics; onSaved: () => void }) {
  const toast = useToast()
  const p = e.stripe_fee.partner
  const name = p.name || 'the partner'
  const [link, setLink] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [email, setEmail] = useState('')
  if (!p.name || p.share_bps <= 0) return <p className="muted" style={{ fontSize: 13 }}>No partner share in the terms, so nothing is sent on.</p>
  const call = async (path: string, body: object = {}) => {
    setBusy(true)
    try { return await api<{ url?: string }>(path, { method: 'POST', body: JSON.stringify(body) }) } catch (err) { toast((err as Error).message, 'neg'); return null } finally { setBusy(false) }
  }
  const makeLink = async () => { const r = await call('/api/spec/partner-stripe/link', { email }); if (r?.url) setLink(r.url) }
  const copy = async () => { try { await navigator.clipboard.writeText(link || ''); toast('Link copied', 'pos') } catch { toast('Select the link and copy it', 'neg') } }
  const day = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

  return (
    <div className="partner-box">
      <div className="spread" style={{ alignItems: 'baseline', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
        <strong>{name}'s {bpsPct(p.of_payment_bps)}, sent to {name}'s Stripe</strong>
        <span className={`pill ${p.confirmed_at ? 'is-good' : p.account_id ? 'is-warn' : ''}`.trim()}>{p.confirmed_at ? 'Sending' : p.account_id ? 'Waiting for you to confirm' : 'Not connected'}</span>
      </div>

      {!p.account_id && (
        <>
          <p className="muted">Send {name} a link to set up {name}'s own Stripe account on Stripe's pages, about 10 minutes. {name} does not need a login here. The link works for 7 days.</p>
          {link ? (
            <div className="stack-tight">
              <input className="field" readOnly value={link} onFocus={(ev) => ev.target.select()} aria-label={`Link for ${name}`} />
              <div className="row-tight"><Button variant="secondary" onClick={copy}>Copy link</Button><Button variant="ghost" onClick={() => setLink(null)}>Done</Button></div>
            </div>
          ) : (
            <div className="stack-tight">
              <Field label={`${name}'s email, for the Stripe account`} type="email" value={email} onChange={(ev) => setEmail(ev.target.value)} />
              <div><Button variant="secondary" onClick={makeLink} disabled={busy || !email.includes('@')}>Make {name}'s link</Button></div>
            </div>
          )}
        </>
      )}

      {p.account_id && !p.confirmed_at && (
        <>
          <p>A Stripe account was connected{p.connected_at ? ` on ${day(p.connected_at)}` : ''}: <strong>{p.label || p.account_id}</strong> <span className="muted">({p.account_id})</span>. Is this {name}'s own account? Nothing is sent until you confirm.</p>
          <div className="row-tight">
            <Button onClick={async () => { if (await call('/api/spec/partner-stripe/confirm', { account_id: p.account_id })) { toast(`Confirmed. ${name}'s share is sent automatically from now on`, 'pos'); onSaved() } }} disabled={busy}>Yes, it is {name}'s</Button>
            <Button variant="ghost" onClick={async () => { if (await call('/api/spec/partner-stripe/disconnect')) onSaved() }} disabled={busy}>No, disconnect it</Button>
          </div>
        </>
      )}

      {p.confirmed_at && (
        <>
          <p className="muted">To {p.label || p.account_id}, confirmed {day(p.confirmed_at)}. Each share is sent once SPEC's fee becomes available in SPEC's Stripe, usually about two business days after the payment.</p>
          <div className="fee-grid">
            <div><span className="hero-kicker">Sent this month</span><strong>{money(p.sent_month_cents)}</strong><span className="muted">{money(p.sent_all_cents)} all time</span></div>
            <div><span className="hero-kicker">Waiting</span><strong>{money(p.waiting_cents)}</strong><span className="muted">sent when SPEC's fee is available</span></div>
            <div><span className="hero-kicker">Did not go through</span><strong>{p.failed}</strong><span className="muted">{p.last_error || 'none'}</span></div>
          </div>
          <div className="row-tight" style={{ marginTop: 'var(--s-4)' }}>
            <Button variant="ghost" onClick={async () => { if (await call('/api/spec/partner-stripe/disconnect')) onSaved() }} disabled={busy}>Stop sending to {name}</Button>
          </div>
        </>
      )}
    </div>
  )
}
